import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { NewsItem } from '../../../models/news-item.model';
import { NewsService } from '../../../services/news.service';
import { Breakpoints } from '../../../enums/breakpoints.enum';
import { ContentState } from '../../../store/state/content.state';
import { RequestUtil } from '../../../utils/request.util';
import { getSectionFromPath, setTitleComponent, titleLabels } from '../../../utils/titles.util';

@Component({
    selector: 'u4-cpp-news-detail',
    templateUrl: './news-detail.component.html'
})
export class NewsDetailComponent implements OnInit, OnDestroy {
    public id: string;
    public newsItem: Observable<NewsItem>;
    public breakpoints = Breakpoints;
    public labels: {[key: string]: string};
    public businessCardHeader: string;
    private destroy$: Subject<void> = new Subject<void>();

    constructor(
        private route: ActivatedRoute,
        private newsService: NewsService,
        private requestUtil: RequestUtil,
        private domSanitizer: DomSanitizer,
        private store: Store<ContentState>,
        private titleService: Title
    ) {
        const section = getSectionFromPath(window.location.pathname, 1);

        if (titleLabels.get(section.toLowerCase()) === undefined) {
            this.titleService.setTitle(setTitleComponent(titleLabels.get('news')));
        } else {
            this.titleService.setTitle(setTitleComponent(titleLabels.get(section.toLowerCase()).concat(' - ' + titleLabels.get('news'))));
        }
        this.store.select(state => state.labels).pipe(
            takeUntil(this.destroy$)
        ).subscribe(labels => this.labels = labels);
    }

    ngOnInit() {
        this.id = this.route.snapshot.paramMap.get('news-id');

        this.newsItem = this.requestUtil.request(this.newsService.getNewsItem(this.id))
            .pipe(
                map((newsItem: { content: SafeHtml } ) => {
                    newsItem.content = this.domSanitizer.bypassSecurityTrustHtml(newsItem.content as string);
                    return newsItem;
                }),
                tap(newsItem => this.canonicalizeNewsUrl(newsItem as NewsItem)),
                tap(newsItem => this.newsService.selectedNewsItem.next(newsItem as NewsItem)
                )
            ) as Observable<NewsItem>;

        this.businessCardHeader = this.labels['news.sales.accountmanager'];
    }

    /**
     * Rewrites the trailing news-id path segment from a legacy (Drupal) id to the canonical
     * Hygraph id once the item resolves. Uses replaceState so the address bar / shareable link
     * is corrected without re-navigating or re-fetching.
     * @param {NewsItem} item the resolved news item (carries the canonical id)
     */
    private canonicalizeNewsUrl(item: NewsItem): void {
        const canonicalId = item?.id;
        if (canonicalId && canonicalId !== this.id && window.location.pathname.endsWith('/' + this.id)) {
            const path = window.location.pathname.replace('/' + this.id, '/' + canonicalId);
            window.history.replaceState(history.state, '', path + window.location.search);
        }
    }

    ngOnDestroy() {
        this.newsService.selectedNewsItem.next(null);
        this.destroy$.next();
        this.destroy$.complete();
    }
}
